// frontend/src/api/client.ts
// ВЕРСИЯ: 2.0.1 (2026-01-25)
// ИЗМЕНЕНИЯ:
// - v2.0.1: Добавлен ApiHttpError (status, body, url) для типизированной обработки HTTP ошибок
// - v2.0.1: Сохранение тела ответа (body) при ошибках и попытка парсинга JSON
// - v2.0.1: credentials: 'include' (на случай cookie-сессий)
// - v2.0.1: Опционально добавляет Authorization: Bearer из localStorage/sessionStorage (только same-origin)

import { API_BASE_URL } from '../config/paths';

export interface HttpOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

export class ApiHttpError extends Error {
  public readonly status: number;
  public readonly body: unknown;
  public readonly url: string;

  constructor(status: number, message: string, body: unknown, url: string) {
    super(message);
    this.name = 'ApiHttpError';
    this.status = status;
    this.body = body;
    this.url = url;

    // Для корректного instanceof в некоторых целях сборки/транспиляции
    Object.setPrototypeOf(this, ApiHttpError.prototype);
  }
}

const BASE =
  ((import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE as
    | string
    | undefined) || API_BASE_URL;

function getStoredToken(): string | null {
  try {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  } catch {
    return null;
  }
}

function isSameOrigin(url: string): boolean {
  try {
    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin;
  } catch {
    return true;
  }
}

function buildErrorMessage(status: number, parsed: unknown, raw: string): string {
  if (parsed && typeof parsed === 'object') {
    const maybeMessage = (parsed as Record<string, unknown>).message;
    if (typeof maybeMessage === 'string' && maybeMessage.trim()) {
      return `HTTP ${status}: ${maybeMessage}`;
    }
    if (Array.isArray(maybeMessage) && maybeMessage.length > 0) {
      return `HTTP ${status}: ${maybeMessage.map(String).join(', ')}`;
    }
  }

  if (raw && raw.trim()) {
    return `HTTP ${status}: ${raw}`;
  }

  return `HTTP ${status}`;
}

async function readAndParseBody(res: Response): Promise<{ raw: string; parsed: unknown }> {
  const raw = await res.text();
  if (!raw) {
    return { raw: '', parsed: undefined };
  }

  const contentType = res.headers.get('content-type') || '';
  const looksJson = contentType.includes('application/json') || contentType.includes('+json');

  if (looksJson) {
    try {
      return { raw, parsed: JSON.parse(raw) };
    } catch {
      return { raw, parsed: raw };
    }
  }

  // Иногда backend может вернуть JSON с неидеальным content-type — попробуем распарсить
  try {
    return { raw, parsed: JSON.parse(raw) };
  } catch {
    return { raw, parsed: raw };
  }
}

async function http<T>(path: string, opts: HttpOptions): Promise<T> {
  const url = path.startsWith('http://') || path.startsWith('https://') ? path : `${BASE}${path}`;

  const headers: Record<string, string> = Object.assign({}, opts.headers || {});

  const token = getStoredToken();
  if (token && !headers.Authorization && isSameOrigin(url)) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (!headers['Content-Type'] && opts.method !== 'GET' && opts.method !== 'DELETE') {
    headers['Content-Type'] = 'application/json';
  }

  const init: RequestInit = {
    method: opts.method,
    headers,
    credentials: 'include',
  };

  if (opts.body !== undefined) {
    init.body = typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body);
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
  }

  const res = await fetch(url, init);
  const { raw, parsed } = await readAndParseBody(res);

  if (!res.ok) {
    throw new ApiHttpError(res.status, buildErrorMessage(res.status, parsed, raw), parsed, url);
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string, headers?: Record<string, string>) =>
    http<T>(path, { method: 'GET', headers }),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    http<T>(path, { method: 'POST', body, headers }),
};

export default api;
